// 🔒 Secure Logger - Ukrywa wrażliwe dane w console
// Pokazuje tylko funkcje i błędy, ukrywa dane użytkownika i klucze

class SecureLogger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
    
    // Lista wrażliwych kluczy do ukrycia
    this.sensitiveKeys = [
      'password', 'token', 'key', 'secret', 'auth', 'uid', 'email', 
      'phone', 'address', 'firstName', 'lastName', 'displayName',
      'photoURL', 'providerData', 'accessToken', 'refreshToken',
      'idToken', 'credential', 'user', 'userData', 'userObject',
      'apiKey', 'authDomain', 'projectId', 'storageBucket',
      'messagingSenderId', 'appId', 'measurementId'
    ];
  }

  // Sprawdza czy klucz jest wrażliwy
  isSensitiveKey(key) {
    if (typeof key !== 'string') return false;
    return this.sensitiveKeys.some(sensitiveKey => 
      key.toLowerCase().includes(sensitiveKey.toLowerCase())
    );
  }

  // Sprawdza czy wartość zawiera wrażliwe dane
  isSensitiveValue(value) {
    if (typeof value === 'string') {
      // Sprawdź czy to email
      if (value.includes('@') && value.includes('.')) return true;
      // Sprawdź czy to token (długi string z różnymi znakami)
      if (value.length > 20 && /[A-Za-z0-9_-]/.test(value)) return true;
      // Sprawdź czy to UID Firebase (28 znaków)
      if (value.length === 28 && /^[A-Za-z0-9]+$/.test(value)) return true;
    }
    return false;
  }

  // Czyści obiekt z wrażliwych danych
  sanitizeObject(obj, maxDepth = 3, currentDepth = 0) {
    if (currentDepth >= maxDepth) return '[Max depth reached]';
    
    if (obj === null || obj === undefined) return obj;
    
    if (typeof obj === 'string') {
      return this.isSensitiveValue(obj) ? '[HIDDEN]' : obj;
    }
    
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return obj;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item, maxDepth, currentDepth + 1));
    }
    
    if (typeof obj === 'object') {
      const sanitized = {};
      
      for (const [key, value] of Object.entries(obj)) {
        if (this.isSensitiveKey(key)) {
          sanitized[key] = '[HIDDEN]';
        } else if (value && typeof value === 'object') {
          sanitized[key] = this.sanitizeObject(value, maxDepth, currentDepth + 1);
        } else if (this.isSensitiveValue(value)) {
          sanitized[key] = '[HIDDEN]';
        } else {
          sanitized[key] = value;
        }
      }
      
      return sanitized;
    }
    
    return obj;
  }

  // Formatuje argumenty do bezpiecznego wyświetlenia
  formatArgs(args) {
    return args.map(arg => {
      if (typeof arg === 'string') {
        return this.isSensitiveValue(arg) ? '[HIDDEN]' : arg;
      }
      if (typeof arg === 'object' && arg !== null) {
        return this.sanitizeObject(arg);
      }
      return arg;
    });
  }

  // Główne metody logowania
  info(message, ...args) {
    if (this.isProduction) return; // Ukryj w produkcji
    
    const safeArgs = this.formatArgs(args);
    console.log(`ℹ️ ${message}`, ...safeArgs);
  }

  debug(message, ...args) {
    if (!this.isDevelopment) return; // Tylko w development
    
    const safeArgs = this.formatArgs(args);
    console.log(`🔍 ${message}`, ...safeArgs);
  }

  function(functionName, ...args) {
    if (this.isProduction) return; // Ukryj w produkcji
    
    const safeArgs = this.formatArgs(args);
    console.log(`⚙️ Function: ${functionName}`, ...safeArgs);
  }

  error(message, error, ...args) {
    // Błędy zawsze pokazuj (ale bez wrażliwych danych)
    const safeArgs = this.formatArgs(args);
    
    if (error && error.stack) {
      console.error(`❌ ${message}`, error.message, ...safeArgs);
      if (this.isDevelopment) {
        console.error('Stack trace:', error.stack);
      }
    } else {
      console.error(`❌ ${message}`, error, ...safeArgs);
    }
  }

  warn(message, ...args) {
    const safeArgs = this.formatArgs(args);
    console.warn(`⚠️ ${message}`, ...safeArgs);
  }

  success(message, ...args) {
    if (this.isProduction) return; // Ukryj w produkcji
    
    const safeArgs = this.formatArgs(args);
    console.log(`✅ ${message}`, ...safeArgs);
  }

  // Specjalne metody dla różnych typów logów
  auth(message, ...args) {
    if (this.isProduction) return;
    
    // Dla auth zawsze ukryj szczegóły użytkownika
    const safeArgs = args.map(arg => {
      if (typeof arg === 'object' && arg !== null) {
        return { 
          exists: !!arg,
          type: typeof arg,
          isLoggedIn: !!arg
        };
      }
      return this.isSensitiveValue(arg) ? '[HIDDEN]' : arg;
    });
    
    console.log(`🔐 Auth: ${message}`, ...safeArgs);
  }

  api(message, ...args) {
    if (this.isProduction) return;
    
    const safeArgs = this.formatArgs(args);
    console.log(`🌐 API: ${message}`, ...safeArgs);
  }

  payment(message, ...args) {
    if (this.isProduction) return;
    
    // Dla płatności ukryj wszystkie szczegóły
    const safeArgs = args.map(() => '[PAYMENT_DATA_HIDDEN]');
    console.log(`💳 Payment: ${message}`, ...safeArgs);
  }

  // Metoda do logowania tylko nazw funkcji bez parametrów
  functionCall(functionName) {
    if (this.isProduction) return;
    console.log(`📞 Function called: ${functionName}`);
  }

  // Metoda do logowania stanu bez wrażliwych danych
  state(stateName, hasValue) {
    if (this.isProduction) return;
    console.log(`📊 State: ${stateName} = ${hasValue ? '[HAS_VALUE]' : '[NO_VALUE]'}`);
  }

  // Metoda do logowania tylko błędów w produkcji
  criticalError(message, error) {
    // Krytyczne błędy zawsze loguj
    console.error(`🚨 CRITICAL: ${message}`, error?.message || error);
    
    if (this.isDevelopment && error?.stack) {
      console.error('Stack trace:', error.stack);
    }
  }
}

// Eksportuj singleton
const secureLogger = new SecureLogger();
export default secureLogger;

// Eksportuj także jako named export dla wygody
export { secureLogger as SecureLogger };

